/* eslint-disable no-restricted-imports */
import { useCallback, useEffect, useState } from 'react';

import { Box, Button, CircularProgress, TextField, Typography } from '@material-ui/core';
import { AddShoppingCart as AddShoppingCartIcon } from '@material-ui/icons';

import { Autocomplete } from '@mui/material';
import { CreateProductDialog } from 'components/Contigo';
import PropTypes from 'prop-types';
import { contigoService } from 'services';

import { useStyles } from './styles';

export default function ProductSearchAsync({
  value,
  onChange,
  label = 'Buscar Produto',
  placeholder = 'Digite o part number ou descrição...',
}) {
  const classes = useStyles();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const searchProducts = useCallback(async (searchText) => {
    if (!searchText || searchText.length < 3) {
      setOptions([]);
      return;
    }

    try {
      setLoading(true);
      const response = await contigoService.listComponentSellPrice({
        search: searchText,
        skip: 0,
        take: 20,
        process: 1,
      });

      const products = response.data?.results || [];
      setOptions(Array.isArray(products) ? products : []);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchProducts(inputValue);
    }, 800);

    return () => clearTimeout(timer);
  }, [inputValue, searchProducts]);

  const handleProductCreated = (newProduct) => {
    onChange(newProduct);
    setOptions((prev) => [newProduct, ...prev]);
  };

  return (
    <Box className={classes.container}>
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={1}>
        {label && (
          <Typography variant='h6' gutterBottom>
            {label}
          </Typography>
        )}
        <Button
          variant='outlined'
          color='primary'
          size='small'
          startIcon={<AddShoppingCartIcon />}
          onClick={() => setDialogOpen(true)}
          className={classes.addButton}
        >
          Novo Produto
        </Button>
      </Box>

      <Autocomplete
        options={options}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return `${option.part_number || ''} - ${option.description || ''}`;
        }}
        filterOptions={(x) => x}
        loading={loading}
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            variant='outlined'
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color='inherit' size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component='li' {...props}>
            <Box style={{ width: '100%' }}>
              <Typography variant='body1'>
                <strong>{option.part_number}</strong> - {option.description}
              </Typography>
              <Box display='flex' gap={2} flexWrap='wrap' mt={0.5}>
                {option.available_quantity !== undefined && (
                  <Typography variant='body2' color='textSecondary'>
                    Disponível: {option.available_quantity} unidades
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
        noOptionsText={
          inputValue.length < 3
            ? 'Digite pelo menos 3 caracteres para buscar'
            : 'Nenhum produto encontrado'
        }
      />

      <CreateProductDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onProductCreated={handleProductCreated}
      />
    </Box>
  );
}

ProductSearchAsync.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
};
